import { useState } from 'react';
import Navbar from '../../components/player/Navbar.tsx';
import Footer from '../../components/player/Footer.jsx';
import '../../styles/RulesBox.css';
import '../../styles/Navigation.css';
import { usePlayerChrome } from '../../hooks/usePlayerChrome.ts';

type RuleBlock =
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'nested'; intro: string; items: string[] }
  | { type: 'example'; lines: string[] }
  | { type: 'h'; text: string };

type RuleSection = {
  id: number;
  title: string;
  group?: string;
  blocks: RuleBlock[];
};

const SECTIONS: RuleSection[] = [
  {
    id: 1,
    title: 'Bet Results',
    blocks: [
      {
        type: 'p',
        text: 'Every bet ends as Won, Lost, Refunded, Half Won, Half Lost, or Pending. There is no separate Void or Push status. Voids, no-action, and pushes are Refunded.',
      },
      {
        type: 'ul',
        items: [
          'Won / Lost – A straightforward win or loss.',
          'Refunded – Your stake is returned. This covers voids, no-action, and pushes (the result landed exactly on the line).',
          'Half Won / Half Lost – A split stake on quarter or split Asian handicap and total lines (a ¼-point margin).',
          'Pending – Not yet gradable. The fixture is not completed, or the result is still waiting on data.',
        ],
      },
      {
        type: 'nested',
        intro: 'Refunded also covers:',
        items: [
          'A game is canceled and the market was not already decided.',
          'A bet pushes on a whole-number line.',
          'A game ends in a tie on a 2-way moneyline with no draw option.',
          'A listed player has no stats (did not play or was scratched).',
        ],
      },
      {
        type: 'p',
        text: 'A bet that cannot be graded stays Pending or returns a grading error. It does not get a result.',
      },
    ],
  },
  {
    id: 2,
    title: 'Game Completion',
    group: 'General Rules',
    blocks: [
      {
        type: 'p',
        text: 'A market settles once the fixture is marked completed. Grading follows that status, not a clock. Minimum-play or elapsed-time windows are not applied when grading. Those live in the fixture lifecycle, which decides when a fixture is completed or cancelled.',
      },
      {
        type: 'ul',
        items: [
          'Unplayed – Not graded. The game has not started.',
          'Live, Delayed, or Suspended – Markets already decided are graded. The rest stay Pending.',
          'Completed – Settled on the final score and stats.',
          'Cancelled – Undecided markets are Refunded. Markets with a complete final result still settle.',
        ],
      },
      {
        type: 'p',
        text: 'If a fixture is cancelled, bets that were not already decided are Refunded. Markets that were already unequivocally determined settle normally (for example, a First Touchdown that was already scored).',
      },
    ],
  },
  {
    id: 3,
    title: 'Point Spreads & Totals',
    group: 'General Rules',
    blocks: [
      {
        type: 'p',
        text: 'On a 2-way spread or total, a result that lands exactly on a whole-number line is Refunded.',
      },
      {
        type: 'p',
        text: 'On a 3-way spread, a push on the favored side settles as Lost.',
      },
      {
        type: 'example',
        lines: ['Over 45 points', 'Final score = 45', 'Result = Refunded'],
      },
    ],
  },
  {
    id: 4,
    title: 'Moneyline Ties',
    group: 'General Rules',
    blocks: [
      {
        type: 'p',
        text: 'On a 2-way market with no draw option, a tie is Refunded.',
      },
    ],
  },
  {
    id: 5,
    title: 'Odd / Even',
    group: 'General Rules',
    blocks: [
      { type: 'p', text: 'A result of 0 counts as even.' },
    ],
  },
  {
    id: 6,
    title: 'Overtime',
    group: 'General Rules',
    blocks: [
      {
        type: 'p',
        text: 'Full-game markets include overtime and shootouts, taken from the aggregate score.',
      },
      {
        type: 'p',
        text: 'Regulation, period, quarter, and half markets use only those periods. They exclude overtime unless the market name says otherwise, for example “… (Incl. OT)”.',
      },
    ],
  },
  {
    id: 7,
    title: 'Player Props',
    group: 'General Rules',
    blocks: [
      { type: 'p', text: 'On a completed fixture, two rules apply to player props:' },
      {
        type: 'ul',
        items: [
          'A listed player with no stats (did not play or was scratched) is Refunded.',
          'Where substitute-voiding is on, a substitute who did play is also Refunded, with no exception. That includes goalscorer markets: a substitute who scored is Refunded, not Won.',
        ],
      },
      {
        type: 'p',
        text: 'Substitute-voiding is on only for baseball (all player props, plus non-starters), soccer, futsal, and pesäpallo (all player props), and basketball First Basket / First Basket Including FT. All other sports do not void a substitute who played. They only refund players with no stats. Which leagues void non-starters is set per market and listed below.',
      },
    ],
  },
  {
    id: 8,
    title: 'Suspended or Delayed Games',
    group: 'General Rules',
    blocks: [
      { type: 'p', text: 'If a game is delayed or suspended:' },
      {
        type: 'ul',
        items: [
          'Bets that have already been decided will be graded.',
          'Everything else remains pending until the game resumes.',
        ],
      },
      {
        type: 'p',
        text: 'A delayed fixture is cancelled, and undecided bets are Refunded, if it is not resumed in time: 24 hours by default, 36 hours for tennis, and 120 hours (5 days) for football. That window is the only “must resume within N hours” rule. It is not a per-market clock.',
      },
      {
        type: 'nested',
        intro: 'A fixture can also be cancelled because:',
        items: [
          'It never started and stayed delayed past the window.',
          'The harvested teams differ from the teams stored on the fixture.',
          'In baseball, before the game is live, the start date moves to another day.',
        ],
      },
      {
        type: 'p',
        text: 'A fixture flagged as a duplicate of another settles off the canonical fixture’s results and uses its stats.',
      },
      {
        type: 'p',
        text: 'NBA Summer League bets do not grade until the game is fully finished.',
      },
      {
        type: 'p',
        text: 'MMA grades only when the fight is Completed or Cancelled. Any other status stays Pending.',
      },
    ],
  },
  {
    id: 9,
    title: 'Baseball',
    group: 'Sport-Specific Player Rules',
    blocks: [
      { type: 'p', text: 'Player props require:' },
      {
        type: 'ul',
        items: [
          'Pitchers must start the game and throw at least one pitch.',
          'Batters must start the game and record at least one plate appearance.',
        ],
      },
      { type: 'p', text: 'Otherwise, bets are refunded. A substitute is also Refunded.' },
      {
        type: 'p',
        text: 'Player props exist for MLB, MLB All-Star, and the World Baseball Classic only.',
      },
      {
        type: 'p',
        text: 'Moneyline can be Listed or Action. Runlines, Totals, Team Totals, and all 1st 5 Innings wagers are always listed pitchers.',
      },
    ],
  },
  {
    id: 10,
    title: 'Basketball',
    group: 'Sport-Specific Player Rules',
    blocks: [
      { type: 'h', text: 'First Basket markets' },
      {
        type: 'ul',
        items: [
          'Applies to First Basket and First Basket Including FT only.',
          'The player must be on the court when the game starts. A substitute is Refunded.',
        ],
      },
      { type: 'h', text: 'Period or half props' },
      {
        type: 'p',
        text: 'The player must log minutes in that period or half.',
      },
    ],
  },
  {
    id: 11,
    title: 'Soccer, Futsal & Pesäpallo',
    group: 'Sport-Specific Player Rules',
    blocks: [
      {
        type: 'p',
        text: 'All player props require the player to start. A substitute who played is Refunded, including a substitute who scored a goal.',
      },
    ],
  },
  {
    id: 12,
    title: 'Tennis, Badminton & Squash',
    group: 'Retirement / Withdrawals',
    blocks: [
      { type: 'p', text: 'On a retirement or walkover:' },
      {
        type: 'ul',
        items: [
          'The moneyline settles on the player who progresses. The opponent loses.',
          'Unresolved set, game, and prop markets are Refunded.',
        ],
      },
    ],
  },
  {
    id: 13,
    title: 'Esports',
    group: 'Retirement / Withdrawals',
    blocks: [
      { type: 'p', text: 'On a retirement:' },
      {
        type: 'ul',
        items: [
          'The moneyline settles on the winner.',
          'Completed maps are graded.',
          'Unplayed maps and other markets are Refunded.',
        ],
      },
    ],
  },
  {
    id: 14,
    title: 'MMA',
    group: 'Retirement / Withdrawals',
    blocks: [
      {
        type: 'p',
        text: 'A no-contest refunds the whole fixture. Only Completed or Cancelled fights are graded. Any other status stays Pending.',
      },
    ],
  },
  {
    id: 15,
    title: 'Golf',
    group: 'Retirement / Withdrawals',
    blocks: [
      {
        type: 'p',
        text: 'A withdrawn, cut, or disqualified player loses a head-to-head to an opponent who is not a DNF. If both do not finish, the one who played more holes wins. 3-balls are Refunded when 18-hole data is incomplete.',
      },
    ],
  },
  {
    id: 16,
    title: 'Motorsports',
    group: 'Retirement / Withdrawals',
    blocks: [
      {
        type: 'p',
        text: 'DNS and DNF drivers are ranked by laps completed. If those laps are equal, the bet is Refunded. A DNS driver loses to any finisher.',
      },
    ],
  },
  {
    id: 17,
    title: 'Table Tennis, Darts, Snooker & Boxing',
    group: 'Retirement / Withdrawals',
    blocks: [
      {
        type: 'p',
        text: 'These sports have no retirement or walkover void. Markets settle on the official result and the stats that are available.',
      },
    ],
  },
  {
    id: 18,
    title: 'Dead Heats',
    group: 'Other Rules',
    blocks: [
      {
        type: 'p',
        text: 'An automatic dead-heat reduction is applied only for golf, and for golf and motorsport futures. The grade includes how many players tied so the payout can be reduced. Other sports do not compute a dead-heat reduction.',
      },
    ],
  },
  {
    id: 19,
    title: 'Parlays',
    group: 'Other Rules',
    blocks: [
      {
        type: 'p',
        text: 'Each selection is graded on its own. There is no separate parlay or same-game parlay settlement. Multi-player prop markets on one fixture settle like this:',
      },
      {
        type: 'ul',
        items: [
          '(Combo) – Adds the listed players’ stats into one over/under.',
          '(Either) – Wins if either player hits. Refunded only if both push.',
          '(Each) – Wins only if both hit. Loses if either misses. Otherwise Refunded.',
        ],
      },
    ],
  },
  {
    id: 20,
    title: 'Futures',
    group: 'Other Rules',
    blocks: [
      {
        type: 'p',
        text: 'Futures are graded from the tournament leaderboard. They are currently supported for golf and motorsport only. See those sports for the supported bet types.',
      },
    ],
  },
];

function RuleBlocks({ blocks }: { blocks: RuleBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === 'p') {
          return (
            <p className="rules-block-text" key={index}>
              {block.text}
            </p>
          );
        }

        if (block.type === 'h') {
          return (
            <span className="teko rules-block-heading" key={index}>
              {block.text}
            </span>
          );
        }

        if (block.type === 'ul') {
          return (
            <ul className="rules-block-list" key={index}>
              {block.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          );
        }

        if (block.type === 'nested') {
          return (
            <div className="rules-block-nested" key={index}>
              <p className="rules-block-text">{block.intro}</p>
              <ul className="rules-block-list rules-block-list--nested">
                {block.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          );
        }

        return (
          <div className="rules-block-example" key={index}>
            <span className="teko rules-block-example-label">Example</span>
            {block.lines.map((line) => (
              <span className="rules-block-example-line" key={line}>
                {line}
              </span>
            ))}
          </div>
        );
      })}
    </>
  );
}

function RulesBox() {
  usePlayerChrome();
  const [activeIndices, setActiveIndices] = useState<number[]>([0]);

  const toggleSection = (index: number) => {
    setActiveIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  return (
    <div className="rules-box-layout player-layout">
      <Navbar />
      <main className="rules-box-page player-main-surface">
        <header className="rules-box-header">
          <span className="teko rules-box-title">SETTLEMENT RULES</span>
          <span className="rules-box-subtitle">
            These are the rules we use to grade every bet.
          </span>
        </header>

        <div className="rules-box-list">
          {SECTIONS.map((section, index) => {
            const isActive = activeIndices.includes(index);
            const prevGroup = index > 0 ? SECTIONS[index - 1].group : undefined;
            const showGroup = Boolean(section.group && section.group !== prevGroup);

            return (
              <div key={section.id}>
                {showGroup && (
                  <span className="teko rules-box-group">{section.group}</span>
                )}

                <article className={`rules-section ${isActive ? 'active' : ''}`}>
                  <button
                    type="button"
                    className={`teko rules-section-title ${isActive ? 'active' : ''}`}
                    onClick={() => toggleSection(index)}
                    aria-expanded={isActive}
                  >
                    <span>{section.title}</span>
                    <span className="rules-section-icon" aria-hidden="true">
                      {isActive ? '−' : '+'}
                    </span>
                  </button>
                  <div className={`rules-section-content ${isActive ? 'active' : ''}`}>
                    <div className="rules-section-content-inner">
                      <div className="rules-section-body">
                        <RuleBlocks blocks={section.blocks} />
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default RulesBox;
